import React, { useState } from 'react';
import {
  User, Mail, Users, Check, CheckCircle, ArrowRight,
} from 'lucide-react';
import { ADULT_AGE } from '../../utils/constants';
import { requestStudentSignup } from '../../services/studentSignups';
import { suggestEmailFix } from '../../utils/emailSuggest';
import EmailSuggestion from '../ui/EmailSuggestion';
import { useConfirm } from '../../context/ConfirmContext';

const FIELD_CLASS =
  'w-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white py-3.5 pr-4 pl-12 rounded-xl mt-1 outline-none focus:border-blue-500 transition-colors';

const ConsentCheckbox = ({ checked, onChange, children }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <div className="relative flex items-center justify-center mt-0.5 shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer appearance-none w-5 h-5 border-2 border-slate-300 dark:border-slate-600 rounded-md checked:bg-blue-600 checked:border-blue-600 transition-colors cursor-pointer"
      />
      <Check size={14} className="absolute text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
    </div>
    <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-tight select-none mt-0.5">
      {children}
    </span>
  </label>
);

const LinkButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
    className="text-blue-600 dark:text-blue-400 font-bold hover:underline"
  >
    {children}
  </button>
);

/**
 * Öğrenci ön kaydı — hesap hemen açılmaz; eğitmen onaylayınca e-posta ile bilgilendirilir.
 * Onay kutusu üst bileşendeki sözleşme penceresiyle paylaşılır.
 */
const StudentSignupForm = ({
  agreedToTerms, setAgreedToTerms,
  onOpenTerms, onOpenKvkk, onBackToLogin, showToast,
}) => {
  const [form, setForm] = useState({ name: '', email: '', age: '', parent: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const requestConfirm = useConfirm();

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const ageNumber = Number(form.age);
  const isMinor = form.age !== '' && ageNumber < ADULT_AGE;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!agreedToTerms) {
      showToast('Kullanıcı Sözleşmesi ve KVKK Aydınlatma Metni\'ni onaylayın.', 'error');
      return;
    }
    const email = form.email.trim();
    const suggestion = suggestEmailFix(email);
    const confirmed = await requestConfirm({
      singleStep: true,
      title: 'E-posta adresinizi kontrol edin',
      tone: 'warning',
      step1Description: (
        <>
          <p>Giriş kodunuz ve onay e-postası bu adrese gönderilecek:</p>
          <p className="mt-2 font-black text-slate-800 dark:text-white break-all">{email}</p>
          {suggestion && (
            <p className="mt-2 text-amber-700 dark:text-amber-300">
              Bunu mu demek istediniz: <strong className="break-all">{suggestion}</strong>?
            </p>
          )}
          <p className="mt-2">Adres yanlışsa kodlar size ulaşmaz.</p>
        </>
      ),
      confirmLabel: 'Tamam, Kayıt Ol',
      cancelLabel: 'Düzelt',
    });
    if (!confirmed) return;
    setSubmitting(true);
    const result = await requestStudentSignup({
      name: form.name.trim(),
      email: form.email,
      age: ageNumber,
      parent: isMinor ? form.parent.trim() : '',
      termsAccepted: true,
    });
    setSubmitting(false);
    if (result.ok) {
      setDone(result.status);
      return;
    }
    showToast(result.error, 'error');
    if (result.code === 'already-exists') onBackToLogin(form.email.trim());
  };

  if (done) {
    return (
      <div className="text-center py-4">
        <div className="w-16 h-16 mx-auto bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center mb-4">
          <CheckCircle size={32} className="text-emerald-500" />
        </div>
        <h3 className="text-xl font-extrabold text-slate-800 dark:text-white mb-2">
          {done === 'already-pending' ? 'Kaydınız zaten alındı' : 'Kaydınız alındı'}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-6">
          Eğitmeniniz kaydınızı onayladığında <strong className="break-all">{form.email.trim()}</strong> adresine
          e-posta gelecek. Ardından bu adrese gelen kodla giriş yapabilirsiniz.
        </p>
        <button
          type="button"
          onClick={() => onBackToLogin(form.email.trim())}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg"
        >
          Giriş ekranına dön <ArrowRight size={20} />
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-xs font-bold text-slate-400 uppercase">Ad Soyad</label>
        <div className="relative mt-2">
          <User className="absolute left-4 top-4 text-slate-300" size={20} />
          <input type="text" required maxLength={80} value={form.name} onChange={set('name')} aria-label="Ad Soyad" className={FIELD_CLASS} />
        </div>
      </div>
      <div>
        <label className="text-xs font-bold text-slate-400 uppercase">E-posta</label>
        <div className="relative mt-2">
          <Mail className="absolute left-4 top-4 text-slate-300" size={20} />
          <input type="email" required value={form.email} onChange={set('email')} autoComplete="email" aria-label="E-posta" placeholder="Giriş kodunuz bu adrese gelir" className={FIELD_CLASS} />
        </div>
        <EmailSuggestion
          email={form.email}
          onApply={(fixed) => setForm((prev) => ({ ...prev, email: fixed }))}
          className="mt-2"
        />
      </div>
      <div>
        <label className="text-xs font-bold text-slate-400 uppercase">Yaş</label>
        <input
          type="number"
          required
          min={3}
          max={99}
          inputMode="numeric"
          value={form.age}
          onChange={set('age')}
          aria-label="Yaş"
          className={`${FIELD_CLASS} mt-3 pl-4`}
        />
      </div>
      {isMinor && (
        <div>
          <label className="text-xs font-bold text-slate-400 uppercase">Veli / vasi ad soyad</label>
          <div className="relative mt-2">
            <Users className="absolute left-4 top-4 text-slate-300" size={20} />
            <input type="text" required maxLength={80} value={form.parent} onChange={set('parent')} aria-label="Veli ad soyad" className={FIELD_CLASS} />
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            18 yaşından küçükler için formu ve onayları veli/vasi doldurmalıdır.
          </p>
        </div>
      )}

      <div className="space-y-3 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
        <ConsentCheckbox checked={agreedToTerms} onChange={setAgreedToTerms}>
          <LinkButton onClick={onOpenTerms}>Kullanıcı Sözleşmesi</LinkButton>
          &apos;ni ve <LinkButton onClick={onOpenKvkk}>KVKK Aydınlatma Metni</LinkButton>
          &apos;ni okudum, kabul ediyorum{isMinor ? ' (veli/vasi olarak)' : ''}.
        </ConsentCheckbox>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-70 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg mt-2"
      >
        {submitting
          ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          : <>Kayıt Ol <ArrowRight size={20} /></>}
      </button>

      <div className="text-sm pt-4 border-t border-slate-100 dark:border-slate-800 text-center">
        <button
          type="button"
          onClick={() => onBackToLogin('')}
          className="font-bold text-slate-500 hover:text-blue-600"
        >
          Zaten hesabım var, Giriş Yap
        </button>
      </div>
    </form>
  );
};

export default StudentSignupForm;
